import React from 'react';

import {
  Box,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Select,
  Radio,
  RadioGroup,
  Stack,
  Text,
  Switch,
} from '@chakra-ui/react';

import { useColors } from './colors';

const Settings = ({ formData, onInputChange, sftpConnections = [] }) => {
  const { backgroundCardColor, textColor, borderColor, secondaryTextColor } =
    useColors();
  const isLastModified = formData.inventoryDateSource === 'lastModified';

  return (
    <Box
      p={6}
      bg={backgroundCardColor}
      borderRadius="lg"
      boxShadow="sm"
      minH="300px"
    >
      <Heading size="lg" mb={6} color={textColor}>
        Settings
      </Heading>
      <FormControl mb={4} maxW="100%">
        <FormLabel fontSize="sm" color={textColor}>
          SFTP connection for inventory
        </FormLabel>
        <Select
          value={formData.sftpConnectionId || ''}
          onChange={(e) =>
            onInputChange('sftpConnectionId', e.target.value || '')
          }
          placeholder="None (manual upload only)"
          color={textColor}
          borderColor={borderColor}
          _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px blue.400' }}
        >
          {sftpConnections.map((conn) => (
            <option key={conn._id} value={conn._id}>
              {conn.name} {conn.isActive ? '' : '(inactive)'}
            </option>
          ))}
        </Select>
      </FormControl>

      {formData.sftpConnectionId && (
        <FormControl mb={4} maxW="100%">
          <FormLabel fontSize="sm" color={textColor}>
            How to detect inventory file date
          </FormLabel>
          <RadioGroup
            value={formData.inventoryDateSource || 'filename'}
            onChange={(val) => onInputChange('inventoryDateSource', val)}
          >
            <Stack spacing={2}>
              <Radio value="filename" colorScheme="blue" color={textColor}>
                <Text as="span" color={textColor} fontSize="sm">
                  Date from filename
                </Text>
                <Text
                  as="span"
                  display="block"
                  fontSize="xs"
                  color={secondaryTextColor}
                  ml={6}
                >
                  e.g. ACME_inv_20250207.csv — new file per day
                </Text>
              </Radio>
              <Radio value="lastModified" colorScheme="blue" color={textColor}>
                <Text as="span" color={textColor} fontSize="sm">
                  Date from last modified
                </Text>
                <Text
                  as="span"
                  display="block"
                  fontSize="xs"
                  color={secondaryTextColor}
                  ml={6}
                >
                  Single file with fixed name; date from file metadata
                </Text>
              </Radio>
            </Stack>
          </RadioGroup>
          {isLastModified && (
            <FormControl mt={3}>
              <FormLabel fontSize="sm" color={textColor}>
                Exact filename on SFTP
              </FormLabel>
              <Input
                value={formData.inventoryFileName || ''}
                onChange={(e) =>
                  onInputChange('inventoryFileName', e.target.value)
                }
                placeholder="e.g. inventory.csv"
                color={textColor}
                borderColor={borderColor}
                _focus={{
                  borderColor: 'blue.400',
                  boxShadow: '0 0 0 1px blue.400',
                }}
              />
            </FormControl>
          )}
          {!isLastModified && formData.sftpConnectionId && (
            <FormControl mt={3}>
              <FormLabel fontSize="sm" color={textColor}>
                Delete SFTP files older than (days)
              </FormLabel>
              <Input
                type="number"
                min={1}
                max={365}
                value={formData.sftpFileRetentionDays ?? ''}
                onChange={(e) =>
                  onInputChange('sftpFileRetentionDays', e.target.value)
                }
                placeholder="e.g. 15 or 30 — leave empty to keep all"
                color={textColor}
                borderColor={borderColor}
                _focus={{
                  borderColor: 'blue.400',
                  boxShadow: '0 0 0 1px blue.400',
                }}
              />
              <Text fontSize="xs" color={secondaryTextColor} mt={1}>
                Daily job will remove old inventory files from SFTP to free space.
              </Text>
            </FormControl>
          )}
        </FormControl>
      )}

      <FormControl mb={4} maxW="50%">
        <FormLabel fontSize="sm" color={textColor}>
          SKU Prefix
        </FormLabel>
        <Input
          value={formData.skuPrefix}
          onChange={(e) => onInputChange('skuPrefix', e.target.value)}
          color={textColor}
          borderColor={borderColor}
        />
      </FormControl>

      <FormControl mb={4} display="flex" alignItems="center" maxW="50%">
        <Switch
          id="uses-sub-warehouse"
          isChecked={!!formData.usesSubWarehouse}
          onChange={(e) => onInputChange('usesSubWarehouse', e.target.checked)}
          colorScheme="blue"
          mr={3}
        />
        <FormLabel htmlFor="uses-sub-warehouse" mb={0} fontSize="sm" color={textColor}>
          Inventory uses sub-warehouse regions
        </FormLabel>
      </FormControl>

      {formData.usesSubWarehouse ? (
        <>
          <FormControl mb={4} maxW="50%">
            <FormLabel fontSize="sm" color={textColor}>
              Sub-warehouse field label
            </FormLabel>
            <Input
              value={formData.subWarehouseLabel || ''}
              onChange={(e) => onInputChange('subWarehouseLabel', e.target.value)}
              placeholder="e.g. Warehouse region"
              color={textColor}
              borderColor={borderColor}
            />
            <Text fontSize="xs" color={secondaryTextColor} mt={1}>
              Map a column in the inventory file to target field{' '}
              <strong>subWarehouse</strong> in the field map.
            </Text>
          </FormControl>
          <FormControl mb={4} maxW="50%">
            <FormLabel fontSize="sm" color={textColor}>
              Sub-warehouse codes
            </FormLabel>
            <Input
              value={formData.subWarehouseCodesText || ''}
              onChange={(e) => onInputChange('subWarehouseCodesText', e.target.value)}
              placeholder="TX, FL"
              color={textColor}
              borderColor={borderColor}
            />
            <Text fontSize="xs" color={secondaryTextColor} mt={1}>
              Comma-separated codes used in marketplace SKU prefixes (e.g. CARTX-, FL-).
            </Text>
          </FormControl>
        </>
      ) : null}
      <FormControl maxW="50%">
        <FormLabel fontSize="sm" color={textColor}>
          Backup Inventory Subtraction Quantity
        </FormLabel>
        <Input
          type="number"
          value={formData.backupInventorySubtractionQuantity}
          onChange={(e) =>
            onInputChange('backupInventorySubtractionQuantity', e.target.value)
          }
          color={textColor}
          borderColor={borderColor}
        />
      </FormControl>
    </Box>
  );
};

export default Settings;
